import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Activity, Wrench } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import EnrollmentTitlesHealth from './EnrollmentTitlesHealth';
import ReconcilePreviewDialog from './ReconcilePreviewDialog';

const Badge = ({ value, critical }) => {
  const isBad = critical ? value > 0 : false;
  const color = value === 0
    ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
    : isBad
      ? 'bg-rose-100 text-rose-800 border-rose-200'
      : 'bg-amber-100 text-amber-800 border-amber-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${color}`}>
      {value === 0 ? <CheckCircle2 className="w-3 h-3" /> : isBad ? <XCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {value}
    </span>
  );
};

const Row = ({ label, value, critical, help }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
    <div>
      <div className="text-sm text-slate-800 font-medium">{label}</div>
      {help && <div className="text-[11px] text-slate-500">{help}</div>}
    </div>
    <Badge value={value} critical={critical} />
  </div>
);

export default function DnaPayFinanceHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [higiene, setHigiene] = useState(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/finance/health');
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao carregar health.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doSnapshot = async () => {
    setSnapshotting(true);
    try {
      const { data } = await api.post('/finance/snapshot');
      toast.success(`Snapshot salvo · id=${data.id.slice(0, 8)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao snapshot.');
    } finally { setSnapshotting(false); }
  };

  const doBackfill = async (dryRun) => {
    if (!dryRun) {
      const ok = window.confirm('Aplicar backfill REAL de vínculos dnapay_titulos → titles?\n\nGrava finance_events (MIGRATION_LINK) para auditoria. Não apaga nada.');
      if (!ok) return;
    }
    setBackfilling(true);
    try {
      const { data } = await api.post('/finance/backfill/dnapay-link', { dry_run: dryRun });
      setBackfillResult(data);
      toast.success(dryRun
        ? `Dry-run: ${data.would_link_or_linked} vínculos possíveis · ${data.not_found} sem match`
        : `Aplicado: ${data.applied} vínculos criados`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha no backfill.');
    } finally { setBackfilling(false); }
  };

  const fixEnrollmentTitles = async () => {
    setReconcileOpen(true);  // V8.1 · nunca corrige às cegas: abre a prévia detalhada
  };

  const fixEnrollmentTitlesBlind = async () => {
    setFixing(true);
    try {
      const { data } = await api.post('/finance/health/fix/enrollment-titles');
      toast.success(`Títulos gerados: ${data.totals?.created || 0} · atualizados: ${data.totals?.updated || 0} · protegidos: ${data.totals?.locked || 0}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao corrigir títulos de matrícula.');
    } finally { setFixing(false); }
  };

  if (loading && !data) {
    return <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>;
  }
  if (!data) return null;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="finance-health-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Activity className="w-6 h-6 text-purple-600" /> Saúde Financeira do Sistema
        </h1>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="px-3 py-1.5 rounded border border-slate-300 text-sm hover:bg-slate-50 inline-flex items-center gap-1" data-testid="finance-health-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Atualizar
          </button>
        </div>
      </div>

      <div className={`rounded-lg border p-4 ${data.criticos === 0 ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase font-bold text-slate-600">Críticos</div>
            <div className={`text-3xl font-bold ${data.criticos === 0 ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="finance-health-criticos">{data.criticos}</div>
            <div className="text-xs text-slate-600 mt-1">{data.objetivo}</div>
          </div>
          {data.criticos === 0 ? <CheckCircle2 className="w-10 h-10 text-emerald-600" /> : <XCircle className="w-10 h-10 text-rose-600" />}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-xs uppercase font-bold text-slate-500 mb-3">Vínculos estruturais</div>
        <Row label="Títulos canônicos" value={data.totais?.titles || 0} help="db.titles (não deletados)" />
        <Row label="DNAPay órfãos (sem title_id)" value={data.dnapay_orfaos_sem_title_id} critical help="Rode Backfill DNAPay-Link" />
        <Row label="Pagamentos sem title" value={data.pagamentos_sem_title} critical help="Bloqueia rastreabilidade" />
        <Row label="Fiscal sem title" value={data.fiscal_sem_title} help="NFS-e sem FK title_id" />
        <Row label="Pago sem payment_date" value={data.pago_sem_payment_date} critical help="Divergência CNAB × db.titles" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-xs uppercase font-bold text-slate-500 mb-3">Cadastros faltantes (não bloqueiam operação)</div>
        <Row label="Títulos sem aluno" value={data.titles_sem_aluno} />
        <Row label="Títulos sem responsável" value={data.titles_sem_responsavel} />
        <Row label="Títulos sem categoria" value={data.titles_sem_categoria} />
        <Row label="Títulos sem plano" value={data.titles_sem_plano} />
        <Row label="Duplicidades legado marcadas" value={data.duplicidades_legado} />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="health-matriculas-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase font-bold text-slate-500">Matrículas & Rematrículas</div>
          <button onClick={fixEnrollmentTitles} disabled={fixing || !data.matriculas?.disponivel}
            className="px-3 py-1.5 rounded bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="health-fix-enrollment-titles">
            {fixing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}
            Corrigir em 1 clique (com prévia)
          </button>
        </div>
        {data.matriculas?.disponivel ? (
          <>
            <Row label="Processos com plano" value={data.matriculas.processos_com_plano} help="Base de conferência" />
            <Row label="Processos sem título gerado" value={data.matriculas.processos_pendentes} critical
              help="Plano criado mas parcelas não materializadas em db.titles" />
            <Row label="Títulos faltantes" value={data.matriculas.titulos_faltantes} critical />
            <Row label="Planos divergentes (valor/vencimento)" value={data.matriculas.titulos_divergentes}
              help="Título existe mas está diferente do plano vigente" />
            <Row label="Títulos protegidos (pagos/parciais)" value={data.matriculas.titulos_protegidos}
              help="Nunca são alterados pela correção" />
            <Row label="Erros de leitura do plano" value={data.matriculas.erros} critical />
            {(data.matriculas.processos || []).length > 0 && (
              <div className="mt-3 max-h-40 overflow-y-auto text-xs border border-slate-100 rounded" data-testid="health-matriculas-list">
                {data.matriculas.processos.map((p) => (
                  <div key={p.process_id} className="flex items-center justify-between px-2 py-1 border-b border-slate-50 last:border-0">
                    <span className="text-slate-700">{p.student_name || p.process_id.slice(0, 8)}</span>
                    <span className="text-slate-500">
                      {p.error ? p.error : `faltam ${p.created} · divergentes ${p.updated}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-slate-500">Indisponível: {data.matriculas?.erro || 'sem dados'}</div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="health-higiene-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase font-bold text-slate-500">Higiene cadastral · fila da secretaria</div>
          <button onClick={async () => {
            try {
              const { data } = await api.get('/finance/health/higiene-cadastral');
              setHigiene(data);
              toast.success(`${data.total_responsaveis} responsável(is) para corrigir`);
            } catch (e) { toast.error('Falha ao carregar fila cadastral'); }
          }} className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold"
            data-testid="health-load-higiene">Carregar fila</button>
        </div>
        {higiene ? (
          <>
            <Row label="Responsáveis com dados faltando" value={higiene.total_responsaveis} critical
              help="Bloqueiam a próxima remessa Safra" />
            <Row label="Títulos bloqueados por cadastro" value={higiene.total_titulos_bloqueados} critical />
            <Row label="Valor bloqueado" value={`R$ ${Number(higiene.valor_bloqueado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} />
            <div className="mt-3 max-h-72 overflow-y-auto border border-slate-100 rounded"
              data-testid="health-higiene-list">
              {higiene.itens.map((i, n) => (
                <div key={n} className="px-2 py-2 border-b border-slate-50 last:border-0 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-800">{i.responsavel || 'Responsável não vinculado'}</span>
                    <a href={`/v2/alunos/aluno/${i.aluno_id}`} className="text-violet-700 underline shrink-0"
                      data-testid={`higiene-fix-${n}`}>Corrigir cadastro</a>
                  </div>
                  <div className="text-slate-500">
                    {i.aluno} · mat. {i.matricula || '—'} · {i.titulos} título(s) ·
                    {' '}R$ {Number(i.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-amber-700">Falta: {i.campos_faltantes.join(', ')}</div>
                </div>
              ))}
              {higiene.itens.length === 0 && (
                <div className="p-3 text-emerald-700">Nenhuma pendência cadastral. Pode gerar a remessa.</div>
              )}
            </div>
          </>
        ) : (
          <div className="text-sm text-slate-500">
            Clique em "Carregar fila" para listar os responsáveis sem CPF, CEP ou endereço que
            impedem a próxima remessa.
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase font-bold text-slate-500">Cobrança</div>
          <a href="/v2/financeiro/dnapay/titulos?quick=sem_boleto"
            className="text-xs font-semibold text-violet-700 underline" data-testid="health-link-sem-boleto">
            Ver títulos sem boleto
          </a>
        </div>
        <Row label="Títulos abertos" value={data.titles_abertos || 0} help="pendente · vencido · parcial" />
        <Row label="Abertos sem boleto/nosso número" value={data.titles_abertos_sem_boleto || 0}
          help="Gere a remessa Safra ou boleto avulso" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="text-xs uppercase font-bold text-slate-500">Ferramentas de migração</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={doSnapshot} disabled={snapshotting} className="px-3 py-2 rounded bg-slate-100 hover:bg-slate-200 text-sm font-semibold" data-testid="finance-health-snapshot">
            {snapshotting ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
            1. Gravar Snapshot
          </button>
          <button onClick={() => doBackfill(true)} disabled={backfilling} className="px-3 py-2 rounded bg-amber-100 hover:bg-amber-200 text-amber-900 text-sm font-semibold" data-testid="finance-health-backfill-dry">
            {backfilling ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
            2. Backfill DNAPay-Link (dry-run)
          </button>
          <button onClick={() => doBackfill(false)} disabled={backfilling} className="px-3 py-2 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-900 text-sm font-semibold" data-testid="finance-health-backfill-apply">
            {backfilling ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
            3. Aplicar Backfill (grava vínculos)
          </button>
        </div>
        {backfillResult && (
          <div className="text-xs bg-slate-50 border border-slate-200 rounded p-3 font-mono whitespace-pre-wrap" data-testid="finance-health-backfill-result">
            {JSON.stringify(backfillResult, null, 2)}
          </div>
        )}
      </div>

      <EnrollmentTitlesHealth />

      <ReconcilePreviewDialog open={reconcileOpen} onClose={() => setReconcileOpen(false)}
        onDone={() => load()} />
    </div>
  );
}
